import { useEffect, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Users, Fuel, Settings2, Gauge, Palette, Calendar, Briefcase, MapPin, Check, ChevronLeft, ShieldCheck, Zap } from "lucide-react";
import { publicApi, errMsg } from "@/lib/api";
import { defaultDates, inr } from "@/lib/brand";
import { CarGallery } from "@/components/site/CarGallery";
import { PriceRows } from "@/components/site/Shared";
import { Switch } from "@/components/ui/switch";
import { CarReviews } from "@/components/site/CarReviews";
import { RatingBadge } from "@/components/site/Stars";
import { DateTimeField } from "@/components/site/DateTimeField";
import { CarActions } from "@/components/site/CarActions";
import { AlternativeCars } from "@/components/site/AlternativeCars";

function Spec({ icon: Icon, label, value }) {
  return (
    <div className="rounded-2xl border border-hc-sage bg-white p-4">
      <Icon className="h-5 w-5 text-[#FF5A00]" />
      <p className="mt-3 text-xs text-neutral-500">{label}</p>
      <p className="font-semibold">{value}</p>
    </div>
  );
}

function BookingWidget({ car, onDates }) {
  const [params] = useSearchParams();
  const d = defaultDates();
  const [pickup, setPickup] = useState(params.get("pickup") || d.pickup);
  const [ret, setRet] = useState(params.get("return") || d.ret);
  const [protection, setProtection] = useState(params.get("protection") === "1");
  const [withDriver, setWithDriver] = useState(params.get("driver") === "1");
  const [quote, setQuote] = useState(null);
  const [error, setError] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    setError("");
    publicApi.post("/quote", { car_id: car.id, pickup_at: pickup, return_at: ret, protection, with_driver: withDriver })
      .then((r) => { setQuote(r.data); onDates({ pickup, ret, driver: withDriver, unavailable: !r.data.available }); })
      .catch((e) => { setQuote(null); setError(errMsg(e)); onDates({ pickup, ret, driver: withDriver, unavailable: false, error: true }); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [car.id, pickup, ret, protection, withDriver]);

  const airport = withDriver && params.get("airport") === "1" ? "&airport=1" : "";
  const book = () => navigate(`/checkout/${car.id}?pickup=${pickup}&return=${ret}&protection=${protection ? 1 : 0}&driver=${withDriver ? 1 : 0}${airport}`);
  const input = "mt-1 w-full rounded-xl border border-black/10 bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FF5A00]";

  return (
    <div id="book" className="scroll-mt-20 rounded-3xl border border-black/5 bg-white p-6 shadow-xl shadow-black/5 lg:sticky lg:top-24" data-testid="booking-widget">
      <p><span className="font-display text-3xl font-bold" data-testid="booking-hourly-rate">{inr(car.price_per_hour)}</span><span className="text-neutral-500"> /hr</span></p>
      <p className="mt-1 text-xs text-neutral-500">Billed per hour · minimum 4 hours</p>
      <div className="mt-5 grid grid-cols-2 gap-1 rounded-xl bg-neutral-100 p-1" data-testid="booking-mode">
        {[[false, "Self-Drive"], [true, "With Driver"]].map(([v, l]) => (
          <button key={l} type="button" onClick={() => setWithDriver(v)} data-testid={`booking-mode-${v ? "driver" : "self"}`}
            className={`rounded-lg py-2 text-sm font-semibold transition-colors duration-200 ${withDriver === v ? "bg-[#FF5A00] text-white shadow" : "text-neutral-700"}`}>{l}</button>
        ))}
      </div>
      {withDriver && <p className="mt-2 text-xs text-neutral-500" data-testid="booking-driver-note">Professional driver assigned by Hoorecars · ₹1,299 per day</p>}
      <div className="mt-6 grid gap-3">
        <label className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">Pickup
          <DateTimeField testId="booking-pickup" value={pickup} onChange={setPickup} className={input} />
        </label>
        <label className="text-xs font-semibold uppercase tracking-[0.15em] text-neutral-500">Return
          <DateTimeField testId="booking-return" value={ret} onChange={setRet} className={input} />
        </label>
      </div>
      <label className="mt-5 flex cursor-pointer items-start justify-between gap-4 rounded-2xl bg-neutral-50 p-4">
        <span>
          <span className="flex items-center gap-2 text-sm font-semibold"><ShieldCheck className="h-4 w-4 text-[#FF5A00]" /> Trip protection</span>
          <span className="mt-1 block text-xs text-neutral-500">₹499 per trip · caps your damage liability at ₹3,000</span>
        </span>
        <Switch checked={protection} onCheckedChange={setProtection} data-testid="booking-protection-toggle" />
      </label>
      <div className="mt-6">
        {error && <p className="rounded-xl bg-rose-50 p-3 text-sm text-rose-700" data-testid="booking-error">{error}</p>}
        {quote && quote.peak_hours > 0 && (
          <p className="mb-3 flex items-start gap-2 rounded-xl bg-orange-50 p-3 text-xs text-orange-800" data-testid="booking-peak-notice">
            <Zap className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            Peak pricing: {quote.peak_hours} of your {quote.hours} hours fall on a weekend, festival or peak time (+{quote.surge_pct}%).
          </p>
        )}
        {quote && <PriceRows q={quote} />}
        {quote && !quote.available && <p className="mt-3 rounded-xl bg-amber-50 p-3 text-sm text-amber-800" data-testid="booking-unavailable">This car is already booked for these dates. Try different times.</p>}
      </div>
      <button onClick={book} disabled={!quote || !quote.available} data-testid="booking-continue-btn"
        className="mt-6 w-full rounded-full bg-[#FF5A00] py-3.5 font-semibold text-white transition-[background-color,transform] duration-200 hover:bg-[#e65100] active:scale-[.98] disabled:cursor-not-allowed disabled:bg-neutral-300">
        Continue to book
      </button>
      <p className="mt-3 text-center text-xs text-neutral-500">Free cancellation up to 48 hours before pickup</p>
    </div>
  );
}

export default function CarDetail() {
  const { id } = useParams();
  const [car, setCar] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [dates, setDates] = useState(null);
  useEffect(() => {
    setCar(null);
    window.scrollTo(0, 0);
    publicApi.get(`/cars/${id}`).then((r) => setCar(r.data)).catch(() => setNotFound(true));
  }, [id]);

  if (notFound) return <div className="container-x py-24 text-center" data-testid="car-not-found">Car not found. <Link to="/cars" className="text-[#FF5A00]">Browse cars</Link></div>;
  if (!car) return <div className="container-x py-24 text-neutral-500">Loading…</div>;

  return (
    <div className="container-x py-10 pb-24 lg:pb-10" data-testid="car-detail-page">
      <Link to="/cars" className="inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-950" data-testid="back-to-cars"><ChevronLeft className="h-4 w-4" /> All cars</Link>
      <div className="mt-4 grid gap-10 lg:grid-cols-[1.6fr_1fr]">
        <div className="min-w-0 space-y-10">
          <div>
            <p className="label-eyebrow">{car.type} · {car.city}</p>
            <h1 className="mt-2 text-4xl font-bold tracking-tighter sm:text-5xl" data-testid="car-detail-name">{car.name}</h1>
            <p className="mt-1 flex flex-wrap items-center gap-3 text-neutral-600">{[car.variant, car.year].filter(Boolean).join(" · ")}<RatingBadge avg={car.rating_avg || 0} count={car.rating_count || 0} testId="car-detail-rating" /></p>
            <CarActions car={car} />
          </div>
          <CarGallery car={car} />
          <p className="max-w-2xl text-base leading-7 text-neutral-700">{car.description}</p>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Spec icon={Users} label="Seats" value={car.seats} />
            <Spec icon={Settings2} label="Transmission" value={car.transmission} />
            <Spec icon={Fuel} label="Fuel" value={car.fuel} />
            <Spec icon={Gauge} label="Mileage" value={car.mileage} />
            <Spec icon={Palette} label="Colour" value={car.color} />
            <Spec icon={Calendar} label="Model year" value={car.year} />
            <Spec icon={Briefcase} label="Boot space" value={car.boot} />
            <Spec icon={MapPin} label="City" value={car.city} />
          </div>
          <div>
            <h2 className="text-2xl font-semibold tracking-tight">Features</h2>
            <ul className="mt-4 grid gap-3 sm:grid-cols-2">
              {car.features.map((f) => <li key={f} className="flex items-center gap-2 text-sm"><Check className="h-4 w-4 text-[#FF5A00]" />{f}</li>)}
            </ul>
          </div>
          <CarReviews carId={car.id} />
          <div className="rounded-3xl bg-hc-deep p-8 text-white">
            <h2 className="text-2xl font-semibold tracking-tight">Rental terms at a glance</h2>
            <ul className="mt-5 grid gap-3 text-sm text-white/75 sm:grid-cols-2">
              <li>{car.km_per_day} km included per 24 hours (prorated by hour)</li>
              <li>Extra km charged at ₹{car.extra_km_rate}/km</li>
              <li>Refundable deposit: {inr(car.deposit)}</li>
              <li>Return with the same fuel / charge level</li>
              <li>Driver must be 21+ with a valid licence held 1+ year</li>
              <li>Hourly pricing · minimum rental: 4 hours</li>
            </ul>
            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm">
              {[["rental-agreement", "Rental agreement"], ["payments-refunds", "Payments, deposit & refunds"], ["kyc-verification", "Driver verification"]].map(([s, t]) => (
                <Link key={s} to={`/legal/${s}`} className="text-hc-gold hover:underline" data-testid={`car-legal-${s}`}>{t}</Link>
              ))}
            </div>
          </div>
        </div>
        <div><BookingWidget key={car.id} car={car} onDates={setDates} /></div>
      </div>
      <AlternativeCars carId={car.id} dates={dates} />
      <div className="fixed inset-x-0 bottom-0 z-30 flex items-center justify-between border-t border-black/5 bg-white/90 px-5 py-3 backdrop-blur-xl lg:hidden">
        <p><span className="font-display text-xl font-bold">{inr(car.price_per_hour)}</span><span className="text-sm text-neutral-500"> /hr</span></p>
        <a href="#book" data-testid="mobile-book-cta" className="rounded-full bg-[#FF5A00] px-6 py-2.5 text-sm font-semibold text-white">Book now</a>
      </div>
    </div>
  );
}
